import { can, type OrganisationRole } from '@evw/domain';
import { Button, Card } from '@evw/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { archiveCoreRecord, createLocation, listLocations, updateLocation } from '../tenancy/api';

export function LocationsPage({
  organisationId,
  role
}: {
  organisationId: string;
  role: OrganisationRole;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [timeZone, setTimeZone] = useState('Pacific/Auckland');
  const [editingId, setEditingId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => listLocations(organisationId)
  });
  const save = useMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        code: code.trim().toLowerCase(),
        time_zone: timeZone.trim()
      };
      return editingId
        ? updateLocation(organisationId, editingId, input)
        : createLocation(organisationId, input);
    },
    onSuccess: async () => {
      setName('');
      setCode('');
      setTimeZone('Pacific/Auckland');
      setEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ['locations', organisationId] });
    }
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveCoreRecord('locations', organisationId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations', organisationId] })
  });
  const editable = can(role, 'create', 'location');
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <div className="stack">
      <Card>
        <h2>Locations</h2>
        <p className="muted">Places where the organisation operates.</p>
        {editable ? (
          <form className="inline-form" onSubmit={submit}>
            <label>
              Name
              <input
                required
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label>
              Code
              <input
                required
                pattern="[a-z0-9-]+"
                maxLength={40}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            <label>
              Time zone
              <input required value={timeZone} onChange={(e) => setTimeZone(e.target.value)} />
            </label>
            <div className="form-actions">
              <Button disabled={save.isPending}>
                {editingId ? 'Save location' : 'Add location'}
              </Button>
              {editingId ? (
                <Button
                  className="button-secondary"
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setName('');
                    setCode('');
                    setTimeZone('Pacific/Auckland');
                  }}
                >
                  Cancel
                </Button>
              ) : null}
            </div>
          </form>
        ) : null}
        {save.error ? <p className="error">{save.error.message}</p> : null}
      </Card>
      <RecordList loading={query.isLoading} error={query.error?.message} empty="No locations yet.">
        {query.data?.map((location) => (
          <article className="record" key={location.id}>
            <div>
              <strong>{location.name}</strong>
              <p>
                {location.code} · {location.time_zone}
              </p>
            </div>
            {editable ? (
              <div className="record-actions">
                <Button
                  className="button-secondary"
                  onClick={() => {
                    setEditingId(location.id);
                    setName(location.name);
                    setCode(location.code);
                    setTimeZone(location.time_zone);
                  }}
                >
                  Edit
                </Button>
                <Button className="button-secondary" onClick={() => archive.mutate(location.id)}>
                  Archive
                </Button>
              </div>
            ) : null}
          </article>
        ))}
      </RecordList>
    </div>
  );
}

function RecordList({
  loading,
  error,
  empty,
  children
}: {
  loading: boolean;
  error: string | undefined;
  empty: string;
  children?: React.ReactNode;
}) {
  if (loading)
    return (
      <Card>
        <p>Loading…</p>
      </Card>
    );
  if (error)
    return (
      <Card>
        <p className="error">{error}</p>
      </Card>
    );
  return (
    <Card>
      <div className="records">{children || <p className="muted">{empty}</p>}</div>
    </Card>
  );
}
